// Numeric formatting + masking helpers.
//
// Currency formatters (formatMoney, formatPercentage, formatAed) live next
// door in `currencyUtils.ts`. Anything that maps a number to a non-currency
// presentation string belongs here.

const TWO_DECIMAL_PRECISION = 100
const VISIBLE_ACCOUNT_DIGITS = 4

const usDecimalFormatter = new Intl.NumberFormat('en-US')

/**
 * Rounds a number to 2 decimal places. Used by the currency formatters that
 * need to display "AED 1,234.56" cleanly without floating-point artefacts.
 */
export const roundToTwoDecimals = (value: number): number =>
  Math.round(value * TWO_DECIMAL_PRECISION) / TWO_DECIMAL_PRECISION

/**
 * Clamps `value` to the inclusive `[min, max]` range.
 * @example clamp(127, 0, 100) // 100
 * @example clamp(-5, 0, 100)  // 0
 */
export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

/**
 * Strips the country code and whitespace from a full mobile number,
 * returning only the national number.
 */
export function toNationalNumber(mobile: string, countryCode?: string | null): string {
  const stripped = countryCode ? mobile.replace(countryCode, '') : mobile
  return stripped.replace(/\s+/g, '').trim()
}

/**
 * Masks a bank account number, revealing only the last 4 digits.
 * @example maskAccountNumber('1234567890') // '•••• 7890'
 */
export function maskAccountNumber(accountNumber?: string | null): string {
  if (!accountNumber) return '••••'
  return `•••• ${accountNumber.slice(-VISIBLE_ACCOUNT_DIGITS)}`
}

/**
 * Formats a number with US locale grouping ("1,234,567"). Returns "0" for
 * non-numeric input. Use `formatMoney` / `formatAed` for currency.
 */
export function formatToInternational(amount: number | string): string {
  const num = typeof amount === 'string' ? Number(amount) : amount
  if (isNaN(num)) return '0'
  return usDecimalFormatter.format(num)
}
